// src/types/core.ts

/**
 * Represents the base properties that all entities should have
 */
export interface BaseEntity {
    id: string;
    createdAt: Date;
    updatedAt: Date;
}

/**
 * Represents a user's role in the system
 */
export enum UserRole {
    MENTOR = 'MENTOR',
    MENTEE = 'MENTEE',
    ADMIN = 'ADMIN'
}

/**
 * Represents expertise levels
 */
export enum ExpertiseLevel {
    BEGINNER = 'BEGINNER',
    INTERMEDIATE = 'INTERMEDIATE',
    ADVANCED = 'ADVANCED',
    EXPERT = 'EXPERT'
}

/**
 * Represents a user in the system
 */
export interface User extends BaseEntity {
    email: string;
    role: UserRole;
    name?: string;
    company?: string;
    position?: string;
    expertise?: ExpertiseLevel;
    bio?: string;
    isVerified: boolean;
}

/**
 * Represents the application's error states
 */
export enum ErrorType {
    VALIDATION = 'VALIDATION',
    NETWORK = 'NETWORK',
    AUTHENTICATION = 'AUTHENTICATION',
    AUTHORIZATION = 'AUTHORIZATION',
    NOT_FOUND = 'NOT_FOUND',
    SERVER = 'SERVER'
}

/**
 * Custom error class for application errors
 */
export class AppError extends Error {
    constructor(
        public type: ErrorType,
        message: string,
        public statusCode?: number,
        public details?: Record<string, unknown>
    ) {
        super(message);
        this.name = 'AppError';
    }
}

/**
 * Represents API response structure
 */
export interface ApiResponse<T> {
    data: T;
    success: boolean;
    message?: string;
    errors?: Array<{
        field?: string;
        message: string;
    }>;
}

/**
 * Form submission states
 */
export interface FormState {
    isSubmitting: boolean;
    isSubmitted: boolean;
    errors: Record<string, string>;
}

/**
 * Theme configuration
 */
export interface ThemeConfig {
    mode: 'light' | 'dark';
    primaryColor: string;
    secondaryColor: string;
}

/**
 * SEO metadata interface
 */
export interface SEOMetadata {
    title: string;
    description: string;
    keywords: string[];
    ogImage?: string;
    ogTitle?: string;
    ogDescription?: string;
}

/**
 * Navigation item structure
 */
export interface NavigationItem {
    id: string;
    label: string;
    href: string;
    icon?: string;
    children?: NavigationItem[];
    color?: string;
    isExternal?: boolean;
}